/**
 * BuyListTicker — homepage live demand ticker.
 *
 * Fetches anonymized Buy List demand from /api/buy-list/ticker and scrolls
 * the entries horizontally in a dark gold-bordered strip. Hidden entirely
 * when fewer than 3 active requests exist (the API returns an empty list).
 *
 * Animation: the item list is rendered twice; a CSS keyframe animation
 * translates the track from 0 to -50% over ~50s for a seamless loop.
 * Hovering the strip pauses the animation (see index.css).
 *
 * Click behavior:
 *   - Signed-in user      → /seller?tubeFilter={tube_name}  (inventory check)
 *   - Anonymous visitor   → /browse?search={tube_name}      (marketplace search)
 */

import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TickerItem {
  tube_name:          string;
  brand:              string | null;
  condition_tested:   boolean | null;
  quantity:           number;
  max_price:          string;
  shipping_allowance: string;
  created_at:         string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeDate(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(diff / 60_000);
    const hours   = Math.floor(diff / 3_600_000);
    const days    = Math.floor(diff / 86_400_000);
    if (minutes < 2)  return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours   < 24) return `${hours}h ago`;
    if (days    < 7)  return `${days}d ago`;
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function conditionLabel(v: boolean | null): string | null {
  if (v === true)  return "tested";
  if (v === false) return "untested ok";
  return null;
}

function formatPrice(raw: string): string {
  const n = parseFloat(raw);
  if (!isFinite(n) || n <= 0) return "";
  return `≤$${n % 1 === 0 ? n.toLocaleString() : n.toFixed(2)}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function BuyListTicker({ onMarketplaceSearch }: {
  /**
   * Optional override for the anonymous-visitor click. On the Browse page,
   * navigating to /browse?search=… would be a no-op (the page reads URL
   * params only on mount), so Browse passes a handler that applies the tube
   * as the active search filter instead. Signed-in sellers always go to
   * their inventory filter regardless of this prop.
   */
  onMarketplaceSearch?: (tubeName: string) => void;
} = {}) {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [items, setItems] = useState<TickerItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/buy-list/ticker", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() as Promise<{ items: TickerItem[] }> : { items: [] as TickerItem[] })
      .then(d => { if (!cancelled) setItems(d.items ?? []); })
      .catch(() => { /* hidden on failure */ });
    return () => { cancelled = true; };
  }, []);

  function handleItemClick(item: TickerItem) {
    const tube = item.tube_name;
    if (user) {
      navigate(`/seller?tubeFilter=${encodeURIComponent(tube)}`);
    } else if (onMarketplaceSearch) {
      onMarketplaceSearch(tube);
    } else {
      navigate(`/browse?search=${encodeURIComponent(tube)}`);
    }
  }

  if (items.length === 0) return null;

  // Double the list for a seamless CSS wrap-around (track animates 0 → -50%).
  const doubled = [...items, ...items];

  return (
    <div
      className="buy-list-ticker"
      aria-label="Live buyer demand — tubes wanted right now"
      style={{
        background:   "#0a0800",
        borderTop:    "1.5px solid #6b4f10",
        borderBottom: "1.5px solid #6b4f10",
        overflow:     "hidden",
        position:     "relative",
        userSelect:   "none",
        WebkitUserSelect: "none",
      }}
    >
      {/* Right fade */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", right: 0, top: 0, bottom: 0, width: 48,
          background: "linear-gradient(to left, #0a0800 60%, transparent)",
          zIndex: 2, pointerEvents: "none",
        }}
      />

      {/* Fixed header label */}
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0, zIndex: 3,
        display: "flex", alignItems: "center",
        padding: "0 12px",
        background: "#0a0800",
        borderRight: "1px solid #3a2a05",
        boxShadow: "8px 0 12px rgba(10,8,0,0.9)",
        pointerEvents: "none",
      }}>
        <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: "0.12em",
          textTransform: "uppercase", color: "#c9a048",
          whiteSpace: "nowrap",
        }}>
          <span style={{ color: "#e8c070" }}>⬤</span> WANTED
        </span>
      </div>

      {/* Scrolling track */}
      <div style={{ overflow: "hidden", marginLeft: 76 }}>
        <div
          className="buy-list-ticker-track"
          style={{
            display: "inline-flex",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          {doubled.map((item, i) => (
            <button
              key={i}
              onClick={() => handleItemClick(item)}
              title={user
                ? `Check your inventory for ${item.tube_name}`
                : `Search the marketplace for ${item.tube_name}`}
              aria-hidden={i >= items.length}   /* duplicate half is decorative */
              tabIndex={i >= items.length ? -1 : 0}
              style={{
                display:     "inline-flex",
                alignItems:  "center",
                gap:         6,
                padding:     "7px 14px",
                background:  "transparent",
                border:      "none",
                borderRight: "1px solid #2a1e00",
                cursor:      "pointer",
                fontFamily:  "inherit",
                textAlign:   "left",
                flexShrink:  0,
              }}
            >
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
                 textTransform: "uppercase", color: "#73bfff",
                 background: "#102e4b", padding: "2px 5px", borderRadius: 3,
                flexShrink: 0,
              }}>
                Looking to Buy
              </span>

              <span style={{
                fontSize: 13, fontWeight: 700, color: "#e8c070",
                letterSpacing: "0.03em",
              }}>
                {item.tube_name}
              </span>

              {item.brand && (
                <span style={{ fontSize: 11, color: "#8a7550" }}>
                  {item.brand}
                </span>
              )}

              {item.quantity > 1 && (
                <span style={{ fontSize: 11, fontWeight: 700, color: "#d29aff" }}>
                  ×{item.quantity}
                </span>
              )}

              {formatPrice(item.max_price) && (
                <span style={{ fontSize: 11, fontWeight: 700, color: "#7ee08d" }}>
                  {formatPrice(item.max_price)}
                </span>
              )}

              {conditionLabel(item.condition_tested) && (
                <span style={{
                  fontSize: 9, color: "#6a5828",
                  textTransform: "uppercase", letterSpacing: "0.06em",
                  background: "#100e00", padding: "1px 4px", borderRadius: 2,
                  flexShrink: 0,
                }}>
                  {conditionLabel(item.condition_tested)}
                </span>
              )}

              <span style={{ fontSize: 10, color: "#57451a" }}>
                {relativeDate(item.created_at)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
